/**
 * Bảng màu + bộ chữ + logo cho ảnh QR gửi khách.
 *
 * Màu lấy trực tiếp từ 3 ảnh mockup CEO duyệt (lấy mẫu pixel), KHÔNG lấy từ
 * biến CSS của app: ảnh vẽ trên canvas rồi tải về máy khách hàng, nó phải
 * giống hệt nhau ở mọi máy — biến CSS đổi theo theme sáng/tối, đổi là ảnh gửi
 * khách đổi màu theo.
 */

/** Màu dùng chung cho cả 3 mẫu — thương hiệu Gạo Nâu. */
export const BRAND = {
  /** Nâu đậm — nền mẫu 2, gáy mẫu 3, chữ tiêu đề. */
  espresso: "#2E1A14",
  /** Nâu vàng — viền khung QR, nhãn phụ, badge. */
  gold: "#8A5A21",
  /** Vàng nhạt — chấm trang trí gáy mẫu 3. */
  goldSoft: "#C9A66A",
  /** Kem — nền ngoài. */
  cream: "#F4EEE4",
  /** Kem sáng — nền thẻ. */
  creamLight: "#FBF8F3",
  /** Trắng ngà — nền khung QR (phải rất sáng để máy quét bắt được). */
  paper: "#FFFDFA",
  /** Xanh rêu — chữ trên nhãn "đã xác thực". */
  verdant: "#3E6B59",
  /** Xanh rêu nhạt — nền nhãn "đã xác thực". */
  verdantSoft: "#E8F1EC",
  /** Xám chữ phụ. */
  muted: "#6B5B4E",
  /** Nền ô nội dung chuyển khoản (mẫu 1 dùng nền đậm, chữ sáng). */
  inkBox: "#2C2825",
  /** Đỏ cảnh báo — dòng "Không được phép đổi nội dung chuyển khoản". */
  danger: "#C62828",
  white: "#FFFFFF",
} as const;

/**
 * Bộ chữ của ảnh. Be Vietnam Pro là bộ chữ app đã nạp sẵn ở `index.html`;
 * canvas chỉ vẽ được bộ chữ trình duyệt đã tải xong, nên luôn để sẵn đuôi dự
 * phòng hệ thống cho trường hợp mạng hỏng hoặc máy chưa tải kịp.
 */
export const FONT_STACK = '"Be Vietnam Pro", "Segoe UI", system-ui, sans-serif';

/**
 * Kích thước ảnh xuất ra — 4:5, khổ chuẩn của ảnh gửi Zalo/Messenger.
 * Trùng đúng kích thước 3 ảnh mockup.
 */
export const POSTER_WIDTH = 1080;
export const POSTER_HEIGHT = 1350;

/**
 * Mã thương hiệu dùng để chọn logo — trùng đúng `brands.code` máy chủ trả về
 * (chữ THƯỜNG). Hệ bán 4 thương hiệu: Ảnh Thẻ, Profile, Chụp Ảnh, Travel.
 */
export type BrandLogo = "gnat" | "gnpf" | "gnca" | "gntr";

/** Thương hiệu mặc định khi đơn chưa gắn thương hiệu — xem `logoForBrand`. */
const FALLBACK_LOGO: BrandLogo = "gnpf";

type LogoAssets = {
  /** Dấu hiệu thương hiệu — đặt giữa mã QR. */
  emblem: string;
  /** Bản đủ chữ, mực nâu — dùng trên nền sáng. */
  lockup: string;
  /** Bản đủ chữ, mực trắng ngà — BẮT BUỘC khi đặt trên nền nâu đậm; bản mực
   *  nâu đặt lên nền nâu là logo chìm nghỉm, không đọc được. */
  lockupLight: string;
};

/**
 * Đường dẫn ảnh logo. Ảnh nằm trong `public/` nên tải bằng đường dẫn tuyệt
 * đối từ gốc site — canvas cần ảnh cùng nguồn để không bị "nhiễm bẩn" (tainted)
 * làm hỏng bước xuất PNG.
 */
export const LOGO_ASSETS: Record<BrandLogo, LogoAssets> = {
  gnat: {
    emblem: "/images/brand/gaonau-emblem-idphoto.png",
    lockup: "/images/brand/gaonau-lockup-idphoto.png",
    lockupLight: "/images/brand/gaonau-lockup-idphoto-light.png",
  },
  gnpf: {
    emblem: "/images/brand/gaonau-emblem-profile.png",
    lockup: "/images/brand/gaonau-lockup-profile.png",
    lockupLight: "/images/brand/gaonau-lockup-profile-light.png",
  },
  gnca: {
    emblem: "/images/brand/gaonau-emblem-chupanh.png",
    lockup: "/images/brand/gaonau-lockup-chupanh.png",
    lockupLight: "/images/brand/gaonau-lockup-chupanh-light.png",
  },
  gntr: {
    emblem: "/images/brand/gaonau-emblem-travel.png",
    lockup: "/images/brand/gaonau-lockup-travel.png",
    lockupLight: "/images/brand/gaonau-lockup-travel-light.png",
  },
};

/** Mọi thương hiệu có logo — dùng để duyệt bảng trên mà không viết lại tay. */
export const BRAND_LOGO_CODES = Object.keys(LOGO_ASSETS) as BrandLogo[];

/**
 * Thương hiệu của ĐƠN (`orders.brand_code`) → logo.
 *
 * Chọn theo thương hiệu chứ KHÔNG theo nhóm sản phẩm: nhóm không nói được đơn
 * thuộc thương hiệu nào — Chụp Ảnh và Travel đều bán gói nhóm `profile`, chọn
 * theo nhóm là hai thương hiệu đó in logo Profile (CEO chốt 03/09/2026).
 *
 * Không nhận ra thương hiệu thì rơi về Profile:
 *   · đơn chưa gắn thương hiệu (migration 0205 nạp ngược theo mã gói, đơn nào
 *     không khớp bảng `products` thì bỏ trống);
 *   · phiếu thu mở từ màn không kèm đơn.
 * Thà ra logo chung còn hơn để trống giữa mã QR.
 */
export function logoForBrand(brandCode: string | null | undefined): BrandLogo {
  // Chuẩn hoá vì cột brand_code từng có bản chữ hoa (hằng `DefaultBrandCode`
  // viết "GNPF" đã làm chết mọi phiếu thu ở tầng cơ sở dữ liệu, 02/09/2026).
  const code = brandCode?.trim().toLowerCase();
  return code && code in LOGO_ASSETS ? (code as BrandLogo) : FALLBACK_LOGO;
}
